import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { COMPANY } from "@/lib/company";

export default function Header({ onCtaClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-white/90 border-b border-gray-200 shadow-sm" : "bg-white/70 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3 lg:py-4 flex items-center justify-between gap-4">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-3 group">
          <div className="w-10 h-10 lg:w-11 lg:h-11 flex items-center justify-center bg-[#C5A059] text-white font-heading font-semibold text-lg">
            FT
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[14px] lg:text-[16px] font-medium text-neutral-900 tracking-tight">
              Fix Technical Renovation
            </span>
            <span className="text-[10px] lg:text-[11px] uppercase tracking-[0.18em] text-[#C5A059] font-medium">
              Premier Interior Fitting · Dubai
            </span>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden xl:flex items-center gap-6 text-sm text-neutral-700">
          <Link to="/about-us" className="hover:text-[#C5A059] transition-colors">About</Link>
          <a href="/#services" className="hover:text-[#C5A059] transition-colors">Services</a>
          <a href="/#portfolio" className="hover:text-[#C5A059] transition-colors">Portfolio</a>
          <Link to="/areas-we-serve" className="hover:text-[#C5A059] transition-colors">Areas</Link>
          <a href="/#contact" className="hover:text-[#C5A059] transition-colors">Contact</a>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${COMPANY.phoneIntl}`}
            data-testid="header-phone"
            className="flex items-center gap-2 text-sm font-medium text-neutral-800 hover:text-[#C5A059] transition-colors px-2 py-2"
          >
            <Phone className="w-4 h-4" />
            {COMPANY.phone}
          </a>
          <a
            href={COMPANY.whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp"
            className="flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebd5a] text-white px-4 py-2.5 text-sm font-medium transition-all"
          >
            <MessageCircle className="w-4 h-4" />
            WhatsApp
          </a>
          <Button
            data-testid="header-cta"
            onClick={onCtaClick}
            className="bg-[#C5A059] hover:bg-[#A88746] text-white rounded-none px-5 py-5 text-sm font-medium tracking-tight"
          >
            GET MY ROUGH ESTIMATE
          </Button>
        </div>

        <button
          data-testid="header-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 text-neutral-900"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-gray-200 bg-white px-6 py-4 space-y-3" data-testid="mobile-menu">
          <Link to="/about-us" onClick={() => setOpen(false)} className="block py-2 text-neutral-800">About Us</Link>
          <a href="/#services" onClick={() => setOpen(false)} className="block py-2 text-neutral-800">Services</a>
          <a href="/#portfolio" onClick={() => setOpen(false)} className="block py-2 text-neutral-800">Portfolio</a>
          <Link to="/areas-we-serve" onClick={() => setOpen(false)} className="block py-2 text-neutral-800">Areas We Serve</Link>
          <a href={`tel:${COMPANY.phoneIntl}`} data-testid="mobile-phone" className="flex items-center gap-2 py-2 text-neutral-800">
            <Phone className="w-4 h-4 text-[#C5A059]" /> Call {COMPANY.phone}
          </a>
          <a
            href={COMPANY.whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mobile-whatsapp"
            className="flex items-center justify-center gap-2 bg-[#25D366] text-white px-4 py-3 font-medium"
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp Renovation Expert
          </a>
          <Button
            data-testid="mobile-cta"
            onClick={() => { setOpen(false); onCtaClick?.(); }}
            className="w-full bg-[#C5A059] hover:bg-[#A88746] text-white rounded-none py-6 font-medium"
          >
            GET MY ROUGH ESTIMATE
          </Button>
        </div>
      )}
    </header>
  );
}
